import {Component, OnInit, OnDestroy, ViewChild} from '@angular/core';
import { NgForm } from '@angular/forms';
import { EntitlementModel } from '../../shared/models/entitlement.model';
import { Constants } from '../../shared/constants';
import {ConfigService} from '../../shared/services/config/config.service';
import { TmsApiService } from '../../shared/services/tms-api/tms-api.service';
import * as moment from 'moment';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslatePipe } from 'src/app/shared/pipes/translate.pipe';
import { AnalyticsHandler } from '../../shared/libraries/analytics-handler/analytics-handler';
import {LocaleHandler} from '../../shared/libraries/locale-handler/locale-handler';

import '@com/html';

@Component({
  selector: 'app-ticket-assignment',
  templateUrl: './ticket-assignment.component.html',
  styleUrls: ['./ticket-assignment.component.scss']
})
export class TicketAssignmentComponent implements OnInit, OnDestroy {
  /**
   * Entitlement Form
   * @var {NgForm}
   */
  @ViewChild('entitlementsForm') entitlementsForm: NgForm;

  /**
   * Entitlements retrieved from TMS formated with the required data
   * @var {Array<any>}
   */
  public entitlements: Array<any> = [];

  /**
   * Check if entitlement assignment process is in progress
   * @var {boolean}
   */
  public isAssignmentInProgress = false;

  /**
   * @var {Array<string>}
   */
  public generalTmsError: Array<string> = [];

  /**
   * Max length for first and last name inputs
   * @var {any}
   */
  public nameInputsLength: any;

  /**
   * Application configuration object
   * @var {any}
   */
  private _config: any;

  /**
   * @var {Subscription}
   */
  private _generalTmsErrorSubscription: Subscription;

  /**
   * Component constructor
   *
   * @param _tmsApiService
   * @param _router
   * @param _translatePipe
   * @param _analytics
   * @param _configService
   * @param _localeHandler
   */
  constructor(
    private _tmsApiService: TmsApiService,
    private _router: Router,
    private _translatePipe: TranslatePipe,
    private _analytics: AnalyticsHandler,
    private _configService: ConfigService,
    private _localeHandler: LocaleHandler
  ) {
    this._config = this._configService.getConfig();

    this.nameInputsLength = {
      firstName: this._config.firstNameMaxLength,
      lastName: this._config.lastNameMaxLength
    };

    moment.locale(this._localeHandler.getCurrentLanguage());
  }

  /**
   * On init method
   *
   * @returns {void}
   */
  public ngOnInit(): void {
    this._generalTmsErrorSubscription = this._tmsApiService.generalTmsErrorSubject.subscribe(
      (message: string) => {
        if (!!message) {
          this.isAssignmentInProgress = false;
          this.generalTmsError = [this._translatePipe.transform(message)];
        }
      }
    );

    const currentEntitlements = this._tmsApiService.getEntitlements();

    for (const entitlement of currentEntitlements) {
      this.entitlements.push(this._setEntitlementData(entitlement));
    }

    this._analytics.getModel().then((model: any) => {
      model.pageId = 'assign';
      model.pageVariant = (<any>window).analyticsPageVariant = `${
        this.entitlements.some(e => e.isTicket) ? 'Tickets' : ''
      }${
        this.entitlements.some(e => e.isAnnualPass) ? 'Passes' : '' /*
      }${
        (future logic to detect special events) ? 'SpecialEvent' : '' */
      }Claim`;
      this._analytics.update();
    });

  }

  /**
   * Retrieve the visual ID input error key
   * @param errors
   */
  public getVisualIdInputErrorKey(errors: any, inputName: string): string {
    return `assign-page.input-${inputName}-${Object.keys(errors)[0]}`;
  }

  /**
   * Checks if we should autocomplete links checkbox based on the entitlement type and status
   *
   * @param entitlement
   *
   * @returns {boolean}
   */
  public shouldAutocompleteLinkCheckbox(entitlement: any): boolean {
    return entitlement.isTicket && !entitlement.isLinked;
  }

  /**
   * Changes the entitlement property to link it to the current guest account
   *
   * @param event
   * @param entitlement
   *
   * @returns {void}
   */
  public changeEntitlementLinkStatus(event: CustomEvent, entitlement: any): void {
    entitlement.linkEntitlementToGuestAccount = event.detail.value;
  }

  /**
   * Assigns the entitlements to the current guest
   * @TODO WIP - Review Error handling
   * @returns {void}
   */
  public assignEntitlementsToGuest(): void {
    this._clearGeneralErrors();
    this.isAssignmentInProgress = true;

    this._tmsApiService.confirmAssignments(this.entitlements).subscribe(
      (response) => {
        this._handleConfirmationResponse(response, this.entitlements);
      },
      (error) => {
        console.log(error);
      }
    );
  }

  /**
   * @TODO Review once the final approach is determineed.
   * Back button logic
   */
  public backToVidPage() {
    this._tmsApiService.clearValidatedEntitlements();
    this._router.navigate(
      ['/']
    );
  }

  /**
   * On destroy event
   *
   * @returns {void}
   */
  public ngOnDestroy(): void {
    this.entitlements = [];
    this.isAssignmentInProgress = false;
  }

  /**
   * Sets the entitlement data to show
   *
   * @param entitlement
   * @private
   * @returns {any}
   */
  private _setEntitlementData(entitlement: EntitlementModel): any {
    const isAssigned = entitlement.hasOwnProperty('assignedGuest') &&
      !!entitlement.assignedGuest.firstName &&
      !!entitlement.assignedGuest.lastName;

    return {
      entitlementId: entitlement.entitlementId,
      type: entitlement.category.id,
      productName: entitlement.productName,
      icon: this._getEntitlementIcon(entitlement.category.id),
      isAnnualPass: entitlement.category.id === Constants.ENTITLEMENT_PASS,
      isTicket: entitlement.category.id === Constants.ENTITLEMENT_TICKETS,
      isLinked: entitlement.linked,
      isShared: entitlement.shared,
      isClaimed: (entitlement.linked || entitlement.shared),
      isAssigned: isAssigned,
      assignedTo: {
        fullName: (isAssigned) ? this._generateAssignedGuestFullName(entitlement.assignedGuest) : '',
        firstName: (isAssigned) ? entitlement.assignedGuest.firstName : '',
        lastName: (isAssigned) ? entitlement.assignedGuest.lastName : '',
      },
      expirationDate: (entitlement.category.id === Constants.ENTITLEMENT_PASS) ?
        moment(entitlement.endDateTime).format('MMMM D, YYYY') : null,
      linkEntitlementToGuestAccount: false,
      supplements: entitlement.supplements ? entitlement.supplements : null
    };
  }

  /**
   * Generate the Guest Full name based on the assigned guest data
   * @param {assignedGuest: any}
   */
  private _generateAssignedGuestFullName(assignedGuest: any): string {
    return !!assignedGuest.nickname
      ? assignedGuest.nickname
      : assignedGuest.firstName + ' ' + assignedGuest.lastName;
  }

  /**
   * Gets the product icon based on the entitlement product type
   *
   * @param productType
   * @private
   * @returns {string}
   */
  private _getEntitlementIcon(productType: string): string {
    let entitlementIcon;

    switch (productType) {
      case Constants.ENTITLEMENT_PASS:
        entitlementIcon = Constants.ICON_PASSES;
        break;
      case Constants.ENTITLEMENT_TICKETS:
      default:
        entitlementIcon = Constants.ICON_TICKETS;
        break;
    }

    return entitlementIcon;
  }

  /**
   * @TODO Review logic
   * @param {response:any}
   * @param {confirmedEntitlements:Array<any>}
   */
  private _handleConfirmationResponse(response: any, confirmedEntitlements: Array<any>) {
    const finalEntitlements = [];

    for (const entitlement of confirmedEntitlements) {
      const entId = entitlement.entitlementId,
        currentEntitlement = response[entId];

      if (!!currentEntitlement && !!currentEntitlement.code) {
        const entitlementObj = {
          entitlementId: entId,
          icon: entitlement.icon,
          type: entitlement.type,
          guest: this._getEntitlementAssignedGuest(entitlement),
          productName:  entitlement.productName,
          isOwned: entitlement.linkEntitlementToGuestAccount,
          status: currentEntitlement.code,
          message: currentEntitlement.message,
          supplements: entitlement.supplements ? entitlement.supplements : null
        };

        finalEntitlements.push(entitlementObj);
      }
    }

    if (this._isConfirmationResponseValid(finalEntitlements)) {
      this._tmsApiService.confirmedEntitlementsSubject.next(finalEntitlements);

      this._router.navigate(
        ['/confirm']
      );
    } else {
      this.generalTmsError = [
        this._translatePipe.transform(Constants.GENERIC_ERROR)
      ];

      this.isAssignmentInProgress = false;
    }
  }

  /**
   * Determines if at least one of the Visual ID was successfully linked
   * @param {entitlements:any}
   */
  private _isConfirmationResponseValid(entitlements: any) {
    let isValid = false;

    for (const entitlement of entitlements) {
      isValid = entitlement.status === 200 ? true : false;
      break;
    }

    return isValid;
  }

  /**
   * @TODO Review logic
   * Retrieve the current assigned guest to confirm the assignments
   * @param {entitlement:any}
   */
  private _getEntitlementAssignedGuest(entitlement: any) {
    let name: string = null;
    const currentGuest = entitlement.assignedTo;

    if (
      !!currentGuest &&
      !!currentGuest.firstName &&
      !!currentGuest.lastName
    ) {
      name = currentGuest.firstName + ' ' + currentGuest.lastName;
    } else {
      name = 'The entitlement has been linked to this account';
    }

    return name;
  }

  /**
   * Reset errors displayed at the top of the page.
   */
  private _clearGeneralErrors() {
    this.generalTmsError = [];
  }

  /**
   * @desc Gets the addon names for display
   * @param {Object} supplements
   * @return {String}
   */
  public getAddOnNames(supplements) {
    const names = [];

    supplements.forEach(supplement => {
      names.push(supplement.name);
    });
    const sortedNames = names.sort().join(', ');

    return sortedNames;
  }

  /**
   * Construct a string from product and addons names
   * which reads by Screen Readers
   * @param {EntitlementModel} entitlement
   * @returns {String}
   */
   public getScreenReaderProductAddonsLabels(entitlement: EntitlementModel): string {
    let productAddonsLabelText: string = entitlement.productName || '';
    const supplements = entitlement.supplements;

    if (supplements && supplements.length > 0 && productAddonsLabelText.length > 0) {
      productAddonsLabelText += ` - ${this._translatePipe.transform(
        'assign-page.addons-label'
      )} ${this.getAddOnNames(supplements)}`;
    }

    return productAddonsLabelText;
  }
}
